import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback, TextInput } from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { useLocation } from 'react-router-dom';
import { Form, Field } from '@progress/kendo-react-form';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import TenantDropdown from '../../../Core/Components/TenantDropdown/TenantDropdown';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';
import { listRFDevice } from '../Services/RFDeviceService';
/**
 * Add New RF Device main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New RF Device component.
 */
const AddRFDevice = () => {
  const { tenantID, getTokensFromStorage } = useAuth(); // Get tenantID using Auth Context
  /**
   * Used to handle multiple tabs
   */
  const [selected, setSelected] = React.useState(0);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [tenant, setTenant] = React.useState(findTenantById(tenantID));
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    SRFDeviceId: '',
    TenantId: tenantID,
    TenantName: tenant?.TenantName,
    SerialNum: '',
    MTFDispayWidth: 0,
    MTFDispayHeight: 0,
    LocaleId: '',
    MTFVendorName: '',
  });

  const { state } = useLocation();

  const [tempState, setTempState] = React.useState(state);

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * Get Device Detail from API for edit Device
   */
  React.useEffect(() => {
    if (state) {
      const { accessToken } = getTokensFromStorage();

      const { dataSet } = state;
      (async () => {
        setLoading(true);
        const response = await listRFDevice({
          accessToken: accessToken,
          limit: '50',
          offset: 1,
          filter: [
            {
              ColumnName: 'SRFDeviceId',
              Op: 'eq',
              ColumnValue: dataSet?.SRFDeviceId,
            },
          ],
          isServiceWorker: false,
        });
        const rfDeviceData = response.rfDevices[0];
        setTempState({ ...state, dataSet: rfDeviceData });
        setFormData(rfDeviceData);
        setLoading(false);
      })();
      setFormData({ ...dataSet });
      setTenant(findTenant(dataSet));
      if (ValidatePermissions(dataSet)) {
        setDetailsDataModified(true);
      }
      setShouldUpdate(!shouldUpdate);
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * on update the tenantID update the formData
   */
  React.useEffect(() => {
    if (!state?.edit) {
      let tempTenant = findTenantById(tenantID);
      setTenant(tempTenant);
      setFormData((prevState) => ({
        ...prevState,
        TenantID: tenantID,
        TenantName: tempTenant?.TenantName,
      }));
    }
  }, [tenantID]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };


  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}

          <>
            <div
              className={`add-rf-profile-group main-page-section add-page-section`}
            >
              <Card className='tophead'>
                <CardHeader>
                  <div className='action-buttons-container'>
                    <Link
                      className='action-icon-wrapper !k-d-block'
                      to={`/smartrf/devices`}
                    >
                      <FontAwesomeIcon
                        className='k-mr-2 k-pt-3'
                        icon={faArrowLeft}
                      />
                    </Link>
                    <div>
                      <p className='display-text k-mb-0'>
                        Device: {formData?.SerialNum}
                      </p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
              <Card className='k-mt-3 detail-card'>
                <CardHeader>
                  <h6 className='card-title-secondary'>Details</h6>
                </CardHeader>
                <CardBody className='detail-card-body'>
                  <div>
                    <Form
                      render={(formRenderProps) => (
                        <div className='card-content'>
                          <div className='k-d-flex single-field-row'>
                            <div
                              className={`field-wrapper`}
                            >
                              <TenantDropdown
                                textField='TenantName'
                                label='Tenant'
                                dataItemKey='TenantId'
                                className='tenant-dropdown k-align-self-end'
                                tenant={tenant}
                                setTenant={setTenant}
                                defaultItem={{
                                  TenantName: 'Select Tenant ...',
                                  disabled: true,
                                }}
                                editMode={state?.edit ? 'ReadOnly' : ''}
                                filterable={true}
                                tenantFormId='TenantID'
                                tenantFormName='TenantName'
                                setFormData={setFormData}
                              />
                              
                            </div>
                          </div>
                          <div className='k-d-flex single-field-row'>
                            <div className={`field-wrapper`}>
                              <Field
                                key={'SerialNum'}
                                id={'SerialNum'}
                                name={'SerialNum'}
                                data={formData?.SerialNum}
                                onChange={handleInputChange}
                                label={'Serial Number'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={'ReadOnly'}
                                optional={false}
                              />
                            </div>
                          </div>
                          <div className='k-d-flex single-field-row'>
                            <div className={`field-wrapper`}>
                              <Field
                                key={'LocaleId'}
                                id={'LocaleId'}
                                name={'LocaleId'}
                                data={formData?.LocaleId}
                                onChange={handleInputChange}
                                label={'Locale Id'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={'ReadOnly'}
                                optional={false}
                              />
                            </div>
                            <div className={`field-wrapper`}>
                              <Field
                                key={'MTFVendorName'}
                                id={'MTFVendorName'}
                                name={'MTFVendorName'}
                                data={formData?.MTFVendorName}
                                onChange={handleInputChange}
                                label={'Vendor Name'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={'ReadOnly'}
                                optional={false}
                              />
                            </div>
                          </div>
                          <div className='k-d-flex single-field-row'>
                            <div className={`field-wrapper`}>
                              <Field
                                key={'MTFDispayWidth'}
                                id={'MTFDispayWidth'}
                                name={'MTFDispayWidth'}
                                data={formData?.MTFDispayWidth}
                                onChange={handleInputChange}
                                label={'Dispay Width'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={'ReadOnly'}
                                optional={false}
                              />
                            </div>
                            <div className={`field-wrapper`}>
                              <Field
                                key={'MTFDispayHeight'}
                                id={'MTFDispayHeight'}
                                name={'MTFDispayHeight'}
                                data={formData?.MTFDispayHeight}
                                onChange={handleInputChange}
                                label={'Dispay Height'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={'ReadOnly'}
                                optional={false}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </CardBody>
              </Card>

              {tempState?.edit && (
                <Card className='k-mt-3 detail-grid-section'>
                  <CardBody className='!k-px-0'>
                    <DesktopLayout
                      handleSelect={handleSelect}
                      selected={selected}
                      detailsDataModified={detailsDataModified}
                      state={tempState}
                      shouldUpdate={shouldUpdate}
                      setShouldUpdate={setShouldUpdate}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          </>
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddRFDevice;
